import { cn } from "@/lib/utils";

export type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
};

type SegmentedControlProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
  /** Stretch each option across the parent instead of hugging the label. */
  fill?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

/** Single pill with one highlighted segment at a time (two-or-more option toggle). */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = "sm",
  disabled = false,
  className,
  fill = false,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: SegmentedControlProps<T>) {
  return (
    <div
      id={id}
      className={cn(
        fill
          ? "flex w-full items-stretch rounded-none border-0 bg-transparent p-0"
          : "inline-flex items-stretch rounded-lg border border-slate-200 bg-slate-100 p-0.5",
        size === "sm" ? "text-sm" : "text-base",
        disabled && "opacity-60",
        className,
      )}
      role="group"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-disabled={disabled || undefined}
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            className={cn(
              "font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
              fill
                ? "min-w-0 flex-1 whitespace-normal text-center leading-tight rounded-none px-2 py-2"
                : "shrink-0 whitespace-nowrap rounded-md",
              !fill && (size === "sm" ? "px-3 py-1.5" : "px-4 py-2"),
              selected
                ? fill
                  ? "border border-slate-300 bg-white text-brand-800 shadow-sm"
                  : "bg-white text-brand-800 shadow-sm"
                : fill
                  ? "border border-slate-200 bg-slate-100 text-slate-600 hover:text-slate-900"
                  : "text-slate-600 hover:text-slate-900",
              disabled && "cursor-not-allowed",
            )}
            aria-pressed={selected}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
